import { sessionTime } from "../../utils/sessionTime";

/** A new event that triggers when a user decides to share a link with a friend, so they can join the program.
 * The user can first click on the button that prompts them to invite a friend, and copy the link in the next step.
 * In the action parameter, the value "Start sharing" or "Copy link" can appear.
 */
export const help_friend = (action: "Copy link" | "Start sharing") => ({
  event: "help_friend",
  action: action,
});

/** An event that sends information to the dataLayer when the user enters additional information at the end in the Claim process.
 * This information relates to where the ticket was purchased, language preference and how the user found out about AirHelp.
 * Under the 'type' variable is the selected question the user chooses to answer, while 'label' tells us about the selected option.
 */
export const claim_additional_info = (ticketSource?: string) => ({
  event: "claim_additional_info",
  type: "Ticket purchase",
  label: ticketSource,
});

/** An event that triggers when the user goes through the claim application and compensation is not awarded to the user.
 * A page is then displayed stating that the claim has not been accepted.
 */
export const rejection = (reason?: string) => ({
  event: "rejection",
  reason: reason,
  total_time_claim: sessionTime.getDuration(),
});

/** An event that is triggered at each step of the claim application, when the user correctly completes the data and clicks the 'next' button and proceeds to the next step.
 * The event name is always a constant taking the value: ClaimProcess, in the event action depending on the step and its correctness we can have: EnquiryStepComplete / ValidEnquiry / InvalidEnquiry.
 * On the other hand, the step name information appears in the label.
 */
export const claim_process = (stepName: string, claimProcessAction: string) => ({
  event: "claim_process",
  action: claimProcessAction,
  label: stepName,
});

/** An event that fires when a user registers an account correctly. */
export const sign_up = (method: string, userId?: number) => ({
  event: "sign_up",
  method: method,
  user_id: userId?.toString(),
});

/** An event that should fire when a user clicks on a link that redirects them to a login page.
 * For example, when going through the claim process, the user enters their information and provides an email address that already exists in the database,
 * a button appears at the bottom of the page redirecting to the login page, explaining that an account already exists for that email address.
 */
export const login_click = {
  event: "login_click",
  placement: "Claim - I already have the account",
};

/** An event that is triggered when a user successfully logs into their account. */
export const login = (method: string, userId?: number) => ({
  event: "login",
  method: method,
  user_id: userId?.toString(),
});

/** An event that is triggered when a user searches for a flight and clicks on any of the airports that appear in the prompts.
 * Under the "type" parameter, you will find information on whether the user searched for a take-off airport or an airport where the aircraft landed.
 * In the place of the label, there is information about the airport clicked on from the list that appeared after the phrase was entered.
 */
export const search_airport = (
  type: "departure" | "destination",
  airportName: string,
  searchPhrase?: string,
) => ({
  event: "search_airport",
  type: type,
  label: airportName,
  search_phrase: searchPhrase,
});

/** An event that is triggered when a user clicks on a button. */
export const cta_click = (label: string, placement: string) => ({
  event: "cta_click",
  label,
  placement,
});

/** An event that activates when a user adds a fellow to the claim process. */
export const add_fellow = (count: number) => ({
  event: "add_fellow",
  count,
});

/** An event that is triggered when a user interacts with a page */
export const page_interactions = (action: string, label: string, placement: string) => ({
  event: "page_interactions",
  action,
  label,
  placement,
});
